import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { searchMembers, getMember } from "@/api/members";
import { LOGO_URL } from "@/config";

const nameCache = new Map();

export const MemberTag = ({ guildId, id, testId }) => {
  const [member, setMember] = useState(nameCache.get(id) || null);

  useEffect(() => {
    if (!id || nameCache.has(id)) {
      setMember(nameCache.get(id) || null);
      return;
    }
    let cancelled = false;
    getMember(guildId, id)
      .then((res) => {
        const m = res.member || res;
        nameCache.set(id, m);
        if (!cancelled) setMember(m);
      })
      .catch(() => !cancelled && setMember(null));
    return () => {
      cancelled = true;
    };
  }, [guildId, id]);

  return (
    <span className="inline-flex items-center gap-2" data-testid={testId}>
      {member?.avatar && (
        <img src={member.avatar} alt="" width={20} height={20} loading="lazy" decoding="async" className="h-5 w-5 rounded-full object-cover" />
      )}
      <span>{member ? member.display_name || member.username : id}</span>
      {member?.username && <span className="text-paper-500">@{member.username}</span>}
    </span>
  );
};

export const MemberSelect = ({ guildId, value, onChange, placeholder = "Search members…", testId, includeBots = true }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    if (nameCache.has(value)) {
      setSelected(nameCache.get(value));
      return;
    }
    let cancelled = false;
    getMember(guildId, value)
      .then((res) => {
        const m = res.member || res;
        nameCache.set(value, m);
        if (!cancelled) setSelected(m);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [guildId, value]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchMembers(guildId, query, 25);
        let members = res.members || [];
        if (!includeBots) members = members.filter((m) => !m.bot);
        members.forEach((m) => nameCache.set(m.id, m));
        setResults(members);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, open, guildId, includeBots]);

  if (selected) {
    return (
      <span className="flex w-full items-center justify-between gap-2 rounded-xl border border-white/15 bg-white/[0.03] px-4 py-2.5" data-testid={`${testId}-selected`}>
        <span className="flex min-w-0 items-center gap-2.5">
          <img src={selected.avatar || LOGO_URL} alt="" width={24} height={24} decoding="async" className="h-6 w-6 rounded-full object-cover" />
          <span className="truncate text-sm font-medium text-paper-100">{selected.display_name || selected.username}</span>
          <span className="shrink-0 font-mono text-xs text-paper-500">@{selected.username}</span>
        </span>
        <button
          onClick={() => {
            setSelected(null);
            onChange(null);
            setQuery("");
          }}
          aria-label="Clear selection"
          data-testid={`${testId}-clear`}
          className="shrink-0 rounded-full p-1 text-paper-500 transition-colors hover:text-paper-100"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </span>
    );
  }

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-paper-500" aria-hidden="true" />
        <input
          type="text"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          placeholder={placeholder}
          aria-label={placeholder}
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="w-full rounded-xl border border-white/10 bg-ink-800 py-2.5 pl-11 pr-4 text-sm text-paper-100 placeholder:text-paper-500/60 transition-colors duration-300 focus:border-white/30 focus:outline-none"
        />
      </div>
      {open && (
        <div
          className="absolute z-40 mt-2 max-h-64 w-full overflow-y-auto rounded-xl border border-white/10 bg-ink-850 shadow-2xl"
          role="listbox"
          data-testid={`${testId}-results`}
        >
          {searching && <p className="px-4 py-3 text-xs text-paper-500">Searching…</p>}
          {!searching && results.length === 0 && (
            <p className="px-4 py-3 text-xs text-paper-500">No members found.</p>
          )}
          {!searching &&
            results.map((m) => (
              <button
                key={m.id}
                role="option"
                aria-selected="false"
                onClick={() => {
                  setSelected(m);
                  onChange(m.id);
                  setOpen(false);
                  setQuery("");
                }}
                data-testid={`${testId}-option-${m.id}`}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors duration-150 hover:bg-white/5"
              >
                <img src={m.avatar || LOGO_URL} alt="" width={28} height={28} loading="lazy" decoding="async" className="h-7 w-7 rounded-full object-cover" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-paper-100">{m.display_name || m.username}</span>
                  <span className="block truncate font-mono text-[10px] text-paper-500">
                    @{m.username} · {m.id}
                  </span>
                </span>
                {m.bot && <span className="shrink-0 rounded-full border border-white/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-paper-400">Bot</span>}
              </button>
            ))}
        </div>
      )}
    </div>
  );
};
